import {
  DataClientError,
  isDataClientError,
  type Clock,
  type SessionStorage,
  type StaffSignInStart,
} from '@kk/domain';
import type { HttpClient } from '@kk/api-client';

import type { StaffMe } from '@/types/wire';

import {
  parseStaffSession,
  STAFF_SESSION_KEYS,
  staffSessionIsLive,
  type StaffSession,
} from './staffSession';

/**
 * Milestone-1 staff auth is the API's dev-token stub (research-stack.md §8): the seeded staff member keeps her seed
 * subject; any other address becomes `dev|<email>` and signs in only if the API knows it as staff.
 */
export const STAFF_DEV_SUBJECTS: Readonly<Record<string, string>> = {
  'dana@bgc.example': 'u_dana',
  'admin@bgc.example': 'u_bgc_admin',
};

export function staffDevSubjectFor(email: string): string {
  const key = email.trim().toLowerCase();
  return STAFF_DEV_SUBJECTS[key] ?? `dev|${key}`;
}

const MIN_PASSWORD = 8;

/**
 * Who a token belongs to as STAFF, or the refusal that means "signed in, but not on a club".
 *
 * Two API answers mean that, and the portal only knew one. A person with an account and no memberships
 * gets 200 with `memberships: []`. A person the API has NEVER SEEN — every director opening their first
 * invite, every parent who wanders in — gets 403, because `/v1/org/me` does not create accounts. Both are
 * `INVALID_CREDENTIALS` here, which the sign-in screen draws as "This account isn't on a club"; the 403
 * used to fall through to the generic failure card.
 */
export async function staffMeFor(http: HttpClient, token: string): Promise<StaffMe> {
  let me: StaffMe;
  try {
    me = await http.request<StaffMe>('GET', '/v1/org/me', { token });
  } catch (e) {
    if (isDataClientError(e) && e.code === 'FORBIDDEN')
      throw new DataClientError('INVALID_CREDENTIALS', 'this API has no account for them', e);
    throw e;
  }
  /*
   * **A SUPER ADMIN IS STAFF OF NOTHING, AND THAT IS NOT A REFUSAL** — the third time this project has drawn
   * the A-5 line, and the outermost place it hides.
   *
   * `SUPER_ADMIN_SUBS` admits a person with no membership at all; `MongoOrgService.me` already answers them
   * `{ superAdmin: true, memberships: [] }` precisely so the shell can load. This threw that away one layer
   * further out, so Kiddie Kredit's own staff would have been shown "This account isn't on a club" and would
   * have had no way into the content tool the portal exists to carry.
   *
   * **Found on 2026-09-22 while checking what deleting Test Club would do.** Every super admin today is also
   * staff of that one club, which is the only reason nobody had met this: removing the club removes the
   * membership, and the lockout would have arrived with it — caused by a deletion, and looking nothing like
   * one. Measuring the blast radius of a destructive change is what turned it up, not a test.
   */
  if (
    me.memberships.length === 0 &&
    me.superAdmin !== true &&
    !me.contentRole &&
    !me.contentInvite &&
    !me.contentRequest
  )
    throw new DataClientError('INVALID_CREDENTIALS', 'not a staff member of any organization');
  return me;
}

/** `POST /v1/org/invites/accept` with an identity token: needs no membership, and answers the new one. */
export function acceptInviteWith(http: HttpClient, token: string, inviteToken: string) {
  return http.request<StaffMe>('POST', '/v1/org/invites/accept', {
    token,
    body: { token: inviteToken },
  });
}

export interface StaffAuthDeps {
  http: HttpClient;
  storage: SessionStorage;
  clock: Clock;
}

export interface StaffAuthClient {
  /**
   * How a staff member signs in, and therefore what the sign-in screen draws.
   *
   * - `password` — the local dev stub: a form, and `signIn` is present.
   * - `redirect` — Auth0: one button, and `beginRedirect` / `completeRedirect` are present. There is
   *   no password field, because the portal never sees a password. A form that collects one and hands
   *   it somewhere else is the shape phishing imitates, so the screen does not have one to imitate.
   */
  readonly kind: 'password' | 'redirect' | 'email';
  /**
   * The live session, or null when there is none. **Rejects rather than returning null when there is
   * an account and we are refusing it** (INVALID_CREDENTIALS: signed in at Auth0, staff of nothing) —
   * a refusal with a reason is not the same state as being signed out, and the screen has to tell
   * them apart to say which.
   */
  getSession(): Promise<StaffSession | null>;
  /**
   * Accept a staff invite as whoever is signed in at the identity provider — **staff of something or of
   * nothing**. A person opening their first invite has no membership by definition, so this must never go
   * through `getSession`, whose staff check refuses exactly them (Evan, 2026-09-16: the portal looped on
   * that refusal 730 times). Resolves the new staff session, stored and announced; resolves null when
   * nobody is signed in at all, and rejects with the API's refusal of the invite.
   */
  acceptInvite(inviteToken: string): Promise<StaffSession | null>;
  /** The address the person is signed in with, staff or not — theirs, so safe to show them. */
  currentEmail(): Promise<string | null>;
  /**
   * True when this page load is a RETURN from the identity provider (a code or an error, with its state).
   * A screen must never send someone back to sign in from here: whatever went wrong has to be drawn,
   * because a second trip would come back the same way.
   */
  readonly landedFromCallback: boolean;
  /** `password` clients only. */
  signIn?(input: { email: string; password: string }): Promise<StaffSession>;
  /**
   * `email` clients only (our own sign-in, O00/O00c/O00d): Continue with an address. `google` for a company address
   * (then `beginGoogle`); `code` for every other one, which has been mailed a six-digit code.
   */
  startSignIn?(email: string): Promise<StaffSignInStart>;
  /** `email` clients only: the mailed code signs the person in. */
  verifyCode?(email: string, code: string): Promise<StaffSession>;
  /** `email` clients only: straight to Google (Auth0's Google connection, no Auth0 page). Leaves the page. */
  beginGoogle?(opts: { email?: string; companyOnly: boolean }): Promise<void>;
  /** `email` clients only: whether Google is configured (the clubs portal's "Or continue with Google"). */
  readonly googleAvailable?: boolean;
  /** `redirect` clients only: leaves the page and does not return. */
  beginRedirect?(): Promise<void>;
  /**
   * `redirect` clients only: called at boot when the URL carries a callback. Returns null when there
   * is nothing to complete, so it is safe to call on every load.
   */
  completeRedirect?(): Promise<StaffSession | null>;
  signOut(): Promise<void>;
  lastEmail(): Promise<string | null>;
  onChange(listener: (session: StaffSession | null) => void): () => void;
}

/**
 * `POST /dev/token { sub, email }` (password ≥ 8 chars, not checked by the stub) then `GET /v1/org/me` with that
 * token; an account with no memberships is not staff → INVALID_CREDENTIALS. Auth0 later replaces only signIn.
 */
export class DevTokenStaffAuthClient implements StaffAuthClient {
  readonly kind = 'password' as const;
  private readonly listeners = new Set<(session: StaffSession | null) => void>();
  private announcedExpiry: string | null = null;

  constructor(private readonly deps: StaffAuthDeps) {}

  private emit(session: StaffSession | null): void {
    for (const l of this.listeners) l(session);
  }

  private async readStored(): Promise<StaffSession | null> {
    return parseStaffSession(await this.deps.storage.get(STAFF_SESSION_KEYS.session));
  }

  async getSession(): Promise<StaffSession | null> {
    const stored = await this.readStored();
    if (!stored) return null;
    if (staffSessionIsLive(stored, this.deps.clock.now())) return stored;
    if (this.announcedExpiry !== stored.expiresAt) {
      this.announcedExpiry = stored.expiresAt;
      this.emit(null);
    }
    return null;
  }

  readonly landedFromCallback = false;

  /**
   * The dev stub has no identity outside a staff session — `signIn` refuses a non-staff address before
   * anything is stored — so only an existing staff member can accept here. Local only; Auth0 has no such gap.
   */
  async acceptInvite(inviteToken: string): Promise<StaffSession | null> {
    const stored = await this.readStored();
    if (!stored) return null;
    const me = await acceptInviteWith(this.deps.http, stored.accessToken, inviteToken);
    const session: StaffSession = {
      ...stored,
      user: me.user,
      memberships: me.memberships,
      ...(me.superAdmin ? { superAdmin: true as const } : {}),
      ...(me.contentRole ? { contentRole: me.contentRole } : {}),
      ...(me.contentInvite ? { contentInvite: me.contentInvite } : {}),
      ...(me.contentRequest ? { contentRequest: me.contentRequest } : {}),
    };
    await this.deps.storage.set(STAFF_SESSION_KEYS.session, JSON.stringify(session));
    this.emit(session);
    return session;
  }

  async currentEmail(): Promise<string | null> {
    return (await this.readStored())?.user.email ?? null;
  }

  async lastEmail(): Promise<string | null> {
    return this.deps.storage.get(STAFF_SESSION_KEYS.lastEmail);
  }

  async signIn(input: { email: string; password: string }): Promise<StaffSession> {
    const email = input.email.trim().toLowerCase();
    if (!email || input.password.length < MIN_PASSWORD)
      throw new DataClientError('INVALID_CREDENTIALS');
    let minted: { token: string; expiresAt: string };
    try {
      minted = await this.deps.http.request('POST', '/dev/token', {
        auth: false,
        body: { sub: staffDevSubjectFor(email), email },
      });
    } catch (e) {
      if (isDataClientError(e) && e.code === 'NOT_FOUND')
        throw new DataClientError(
          'NOT_IMPLEMENTED',
          'POST /dev/token is not registered on this API: set CHILD_SESSION_SIGNING_KEY (32+ characters) in apps/api/.env and leave AUTH0_DOMAIN empty',
          e,
        );
      throw e;
    }
    const me = await staffMeFor(this.deps.http, minted.token);
    const session: StaffSession = {
      scope: 'staff',
      accessToken: minted.token,
      expiresAt: minted.expiresAt,
      user: me.user,
      memberships: me.memberships,
      ...(me.superAdmin ? { superAdmin: true } : {}),
      ...(me.contentRole ? { contentRole: me.contentRole } : {}),
      ...(me.contentInvite ? { contentInvite: me.contentInvite } : {}),
      ...(me.contentRequest ? { contentRequest: me.contentRequest } : {}),
    };
    await this.deps.storage.set(STAFF_SESSION_KEYS.session, JSON.stringify(session));
    await this.deps.storage.set(STAFF_SESSION_KEYS.lastEmail, me.user.email);
    this.emit(session);
    return session;
  }

  /** Clears the stored session (the last email stays so the form can prefill it). */
  async signOut(): Promise<void> {
    const current = await this.readStored();
    if (current) await this.deps.storage.set(STAFF_SESSION_KEYS.lastEmail, current.user.email);
    await this.deps.storage.delete(STAFF_SESSION_KEYS.session);
    this.emit(null);
  }

  onChange(listener: (session: StaffSession | null) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }
}
